import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/format";
import { WAREHOUSES } from "@/lib/constants";

const makeLine = (p, quantity = 100) => ({
  _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now() + Math.random()),
  product_id: p.id, quantity, purchase_price: p.cost_price, _name: p.name, _unit: p.unit,
});

export default function BestellingNieuw() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const prefilled = useRef(false);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [receiveWarehouse, setReceiveWarehouse] = useState("vandenbrink");
  const [lines, setLines] = useState([]);
  const [productionDate, setProductionDate] = useState("");
  const [expected, setExpected] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  // Webshopproducten hebben (nog) geen vaste leverancier: altijd selecteerbaar.
  const supplierProducts = products.filter((p) => !supplierId || !p.supplier_id || p.supplier_id === supplierId);

  const addLine = () => {
    if (supplierProducts.length === 0) return;
    setLines((l) => [...l, makeLine(supplierProducts[0])]);
  };

  useEffect(() => {
    const pre = products.find((p) => p.id === params.get("product"));
    if (!pre || prefilled.current) return;
    prefilled.current = true;
    if (params.get("supplier")) setSupplierId(params.get("supplier"));
    setLines([makeLine(pre, Number(params.get("qty")) || 1)]);
  }, [products, params]);
  const updateLine = (idx, patch) => setLines((l) => l.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)));
  const removeLine = (idx) => setLines((l) => l.filter((_, i) => i !== idx));
  const total = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.purchase_price || 0), 0);

  const submit = async () => {
    if (!supplierId) return toast.error("Kies eerst een leverancier.");
    if (lines.length === 0) return toast.error("Voeg minimaal één regel toe.");
    setSaving(true);
    try {
      const res = await api.post("/purchase-orders", {
        supplier_id: supplierId,
        receive_warehouse: receiveWarehouse,
        production_date: productionDate || null,
        expected_delivery_date: expected || null,
        notes,
        lines: lines.map((l) => ({
          product_id: l.product_id,
          quantity: Number(l.quantity),
          purchase_price: Number(l.purchase_price),
        })),
      });
      toast.success(`Bestelling ${res.data.po_number} aangemaakt.`);
      nav(`/bestellingen/${res.data.id}`);
    } catch (e) {
      toast.error("Aanmaken mislukt", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="po-new-page">
      <Link to="/bestellingen" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Terug
      </Link>
      <PageHeader eyebrow="Inkoop" title="Nieuwe bestelling" description="Stel een bestelling op en verzend deze naar de leverancier." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="surface p-6 space-y-4">
            <div>
              <Label className="text-base">Leverancier</Label>
              <Select value={supplierId} onValueChange={(v) => {
                setSupplierId(v);
                setLines((l) => l.filter((ln) => {
                  const p = products.find((x) => x.id === ln.product_id);
                  return !p?.supplier_id || p.supplier_id === v;
                }));
              }}>
                <SelectTrigger className="h-12 mt-2" data-testid="select-supplier">
                  <SelectValue placeholder="Kies leverancier…" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`supplier-option-${s.id}`}>
                      {s.name} {s.preferred && "★"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-base">Ontvangst in magazijn</Label>
              <Select value={receiveWarehouse} onValueChange={setReceiveWarehouse}>
                <SelectTrigger className="h-12 mt-2" data-testid="select-warehouse"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WAREHOUSES.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Productiedatum</Label>
                <Input type="date" value={productionDate} onChange={(e) => setProductionDate(e.target.value)}
                  className="h-12 mt-2" data-testid="production-date" />
              </div>
              <div>
                <Label>Verwachte levering</Label>
                <Input type="date" value={expected} onChange={(e) => setExpected(e.target.value)}
                  className="h-12 mt-2" data-testid="expected-date" />
              </div>
            </div>
            <div>
              <Label>Opmerkingen (optioneel)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Speciale instructies…" className="mt-2" data-testid="po-notes" />
            </div>
          </div>

          <div className="surface overflow-hidden">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold">Orderregels</h3>
              <Button onClick={addLine} className="h-12" data-testid="add-line">
                <Plus className="w-4 h-4 mr-2" /> Regel toevoegen
              </Button>
            </div>
            {lines.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">
                Voeg een productregel toe om te beginnen.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left uppercase tracking-wider text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3 w-32">Aantal</th>
                    <th className="px-4 py-3 w-36">Prijs</th>
                    <th className="px-4 py-3 w-32 text-right">Subtotaal</th>
                    <th className="px-4 py-3 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lines.map((l, idx) => {
                    const subtotal = Number(l.quantity || 0) * Number(l.purchase_price || 0);
                    return (
                      <tr key={l._key} data-testid={`po-line-${idx}`}>
                        <td className="px-4 py-3">
                          <Select value={l.product_id} onValueChange={(v) => {
                            const p = products.find((x) => x.id === v);
                            updateLine(idx, { product_id: v, _name: p.name, _unit: p.unit, purchase_price: p.cost_price });
                          }}>
                            <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {supplierProducts.map((p) => (
                                <SelectItem key={p.id} value={p.id}>{p.article_number} — {p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3">
                          <Input type="number" step="0.1" value={l.quantity}
                            onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                            className="h-11 font-mono" />
                        </td>
                        <td className="px-4 py-3">
                          <Input type="number" step="0.01" value={l.purchase_price}
                            onChange={(e) => updateLine(idx, { purchase_price: e.target.value })}
                            className="h-11 font-mono" />
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold">{formatCurrency(subtotal)}</td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="icon" onClick={() => removeLine(idx)} data-testid={`remove-line-${idx}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="surface p-6">
            <div className="label-eyebrow">Ordertotaal</div>
            <div className="mt-3 font-mono text-4xl font-semibold">{formatCurrency(total)}</div>
            <div className="text-sm text-muted-foreground mt-2">{lines.length} orderregels</div>
            <Button data-testid="save-po" className="w-full mt-6 touch-tap" onClick={submit} disabled={saving}>
              {saving ? "Opslaan…" : "Concept opslaan"}
            </Button>
            <p className="mt-3 text-xs text-muted-foreground">Goedkeuring en verzenden gebeurt vanuit het orderdetail.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
